import type { ReactNode } from 'react';
import { CheckRounded } from '@mui/icons-material';
import { Box, Step, StepLabel, Stepper, type StepIconProps } from '@mui/material';
import { designTokens } from '../tokens';
import { progressMarkerStyles } from '../progressStyles';

export type FlowStepsProps = {
  activeStep: number;
  steps: readonly ReactNode[];
  ariaLabel: string;
  variant?: 'compact' | 'full-width';
  testId?: string;
};

function FlowStepMarker({ active, completed, icon }: StepIconProps) {
  return <Box component="span" aria-hidden="true" sx={progressMarkerStyles(active ? 'active' : completed ? 'success' : 'neutral')}>
    {completed ? <CheckRounded sx={{ fontSize: designTokens.progress.iconSize }} /> : icon}
  </Box>;
}

/** 여러 단계로 구성된 작성 흐름의 현재 위치를 본문 시작선에 맞춰 표시합니다. */
export function FlowSteps({ activeStep, steps, ariaLabel, variant = 'compact', testId }: FlowStepsProps) {
  const compact = variant === 'compact';
  return (
    <Stepper
      component="ol"
      activeStep={activeStep}
      aria-label={ariaLabel}
      data-testid={testId}
      sx={{
        width: compact ? 'fit-content' : '100%',
        maxWidth: `min(${designTokens.layout.flowStepsMaxWidth}px, 100%)`,
        minWidth: 0,
        alignItems: 'flex-start',
        alignSelf: 'flex-start',
        m: 0,
        p: 0,
        listStyle: 'none',
        '& .MuiStep-root': { minWidth: 0, flex: { xs: '1 1 0', sm: '0 1 auto' }, px: { xs: 0.5, sm: 1 } },
        '& .MuiStep-root:first-of-type': { pl: 0 },
        '& .MuiStep-root:last-of-type': { pr: 0 },
        '& .MuiStepConnector-root': { flex: { xs: '0 1 16px', sm: 1 }, minWidth: 8, alignSelf: 'flex-start', mt: `${designTokens.progress.markerSize / 2}px` },
        '& .MuiStepConnector-line': { borderColor: designTokens.color.divider },
        '& .MuiStepLabel-root': { alignItems: compact ? 'flex-start' : { xs: 'center', sm: 'flex-start' }, flexDirection: compact ? 'row' : { xs: 'column', sm: 'row' }, gap: compact ? 0 : { xs: 0.5, sm: 0 } },
        '& .MuiStepLabel-iconContainer': { pr: compact ? 1 : { xs: 0, sm: 1 } },
        '& .MuiStepLabel-labelContainer': { minWidth: 0 },
        '& .MuiStepLabel-label': { ...designTokens.typography.ui, pt: '2px', color: 'text.secondary', wordBreak: 'keep-all', overflowWrap: 'anywhere', textAlign: compact ? 'left' : { xs: 'center', sm: 'left' } },
        '& .MuiStepLabel-label.Mui-active': { color: designTokens.color.actionDark, fontWeight: 600 },
        '& .MuiStepLabel-label.Mui-completed': { color: 'text.primary', fontWeight: 500 },
        ...(compact ? {
          '& .MuiStep-root': { flex: '0 1 auto', minWidth: 0, p: 0 },
          '& .MuiStepConnector-root': { flex: '0 1 28px', minWidth: 8, mx: 1, alignSelf: 'flex-start', mt: `${designTokens.progress.markerSize / 2}px` },
        } : undefined),
      }}
    >
      {steps.map((label, index) => (
        <Step component="li" key={index} aria-current={index === activeStep ? 'step' : undefined}>
          <StepLabel slots={{ stepIcon: FlowStepMarker }}>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}
